import { removeUnsafeLinkHrefsFromDocument } from './html';

function isWordLikeHtml(value: string) {
  return /class=["']?Mso|mso-|urn:schemas-microsoft-com:office|WordSection|StartFragment/i.test(
    value
  );
}

function isForeignDocumentHtml(value: string) {
  return (
    isWordLikeHtml(value) ||
    /docs-internal-guid|Apple-converted-space|LibreOffice|OpenOffice|urn:oasis:names:tc:opendocument/i.test(
      value
    ) ||
    /<h[1-6]\b[^>]*\bstyle=["'][^"']*(?:font-family|font-size|line-height|margin(?:-|:))/i.test(
      value
    )
  );
}

function isIgnorablePasteParagraph(element: Element) {
  const text = element.textContent?.replace(/\u00a0/g, ' ').trim() ?? '';
  const hasMeaningfulElement = Boolean(
    element.querySelector(
      'a,button,code,em,strong,span:not(:empty),table,ul,ol,li,img,object,embed,svg,iframe,video'
    )
  );

  return !text && !hasMeaningfulElement;
}

function removeUnsupportedPasteEmbeds(document: Document) {
  document
    .querySelectorAll('img,object,embed,svg,iframe,video,audio')
    .forEach((element) => element.remove());
}

function replaceElementTag(element: Element, tagName: string) {
  const replacement = element.ownerDocument.createElement(tagName);

  [...element.attributes].forEach((attribute) => {
    replacement.setAttribute(attribute.name, attribute.value);
  });

  while (element.firstChild) {
    replacement.append(element.firstChild);
  }

  element.replaceWith(replacement);
}

function getPastedHeadingTagName(
  tagName: string,
  shouldDemoteDocumentHeadings: boolean
) {
  const tagNamesBySourceTag: Record<string, string> =
    shouldDemoteDocumentHeadings
      ? { h1: 'h2', h2: 'h3' }
      : { h1: 'h2', h2: 'h2', h3: 'h3' };

  return tagNamesBySourceTag[tagName] ?? 'p';
}

function normalizePastedHeadings(
  document: Document,
  shouldDemoteDocumentHeadings: boolean
) {
  document.body.querySelectorAll('h1,h2,h3,h4,h5,h6').forEach((heading) => {
    const tagName = heading.tagName.toLowerCase();
    const targetTagName = getPastedHeadingTagName(
      tagName,
      shouldDemoteDocumentHeadings
    );

    if (targetTagName === tagName) return;

    replaceElementTag(heading, targetTagName);
  });
}

function removeEmptyWordPasteParagraphs(document: Document) {
  document.querySelectorAll('p').forEach((paragraph) => {
    if (isIgnorablePasteParagraph(paragraph)) {
      paragraph.remove();
    }
  });
}

function isEmptyWordParagraphBorderElement(element: Element) {
  const style = element.getAttribute('style') ?? '';
  const text = element.textContent?.replace(/\u00a0/g, ' ').trim() ?? '';

  return (
    /mso-element:\s*para-border-div/i.test(style) &&
    /border-bottom\s*:/i.test(style) &&
    !text
  );
}

function convertWordHorizontalRules(document: Document) {
  document.querySelectorAll('div').forEach((element) => {
    if (isEmptyWordParagraphBorderElement(element)) {
      element.replaceWith(document.createElement('hr'));
    }
  });
}

function convertWordQuotes(document: Document) {
  document.querySelectorAll('p.MsoQuote').forEach((paragraph) => {
    const blockquote = document.createElement('blockquote');
    const normalizedParagraph = document.createElement('p');

    normalizedParagraph.innerHTML = paragraph.innerHTML;
    blockquote.append(normalizedParagraph);
    paragraph.replaceWith(blockquote);
  });
}

function getReferencedAnchorNames(document: Document) {
  return new Set(
    [...document.querySelectorAll('a[href^="#"]')]
      .map((anchor) => anchor.getAttribute('href')?.slice(1).trim() ?? '')
      .filter((anchorName) => anchorName && !/^OLE_LINK/i.test(anchorName))
  );
}

function convertReferencedWordBookmarks(document: Document) {
  const referencedAnchorNames = getReferencedAnchorNames(document);

  if (!referencedAnchorNames.size) return;

  document.querySelectorAll('a[name]:not([href])').forEach((anchor) => {
    const anchorName = anchor.getAttribute('name')?.trim() ?? '';

    if (!referencedAnchorNames.has(anchorName)) return;

    const editorAnchor = document.createElement('a');

    editorAnchor.setAttribute('id', anchorName);
    editorAnchor.setAttribute('name', anchorName);
    anchor.before(editorAnchor);
    anchor.replaceWith(...anchor.childNodes);
  });
}

interface LooseListParagraph {
  paragraph: HTMLParagraphElement;
  kind: 'ul' | 'ol';
  level: number;
}

interface WordListHint {
  listId: string;
  level: number;
}

const WORD_BULLET_MARKER_PATTERN = /^[•·●○◦▪▫\uf0b7\uf0a7\uf0d8]\s+/;
const WORD_LIST_IGNORE_PATTERN = /mso-list\s*:\s*Ignore/i;

function getWordListStyleMap(document: Document) {
  const listStyles = new Map<string, 'ul' | 'ol'>();

  document.querySelectorAll('style').forEach((styleElement) => {
    const styleText = styleElement.textContent ?? '';
    const listDefinitionPattern =
      /@list\s+([^\s:{]+):level(\d+)\s*\{([^}]*)\}/gi;
    let match = listDefinitionPattern.exec(styleText);

    while (match) {
      const listId = match[1] ?? '';
      const level = Number(match[2] ?? '1') - 1;
      const declarations = match[3] ?? '';
      const kind = /mso-level-number-format\s*:\s*bullet/i.test(declarations)
        ? 'ul'
        : 'ol';

      listStyles.set(`${listId}:${level}`, kind);
      match = listDefinitionPattern.exec(styleText);
    }
  });

  return listStyles;
}

function getWordListHint(paragraph: HTMLParagraphElement): WordListHint | null {
  const style = paragraph.getAttribute('style') ?? '';
  const match = style.match(/mso-list\s*:\s*([^\s;]+)\s+level(\d+)/i);

  if (!match?.[1] || !match[2]) return null;

  return {
    listId: match[1],
    level: Math.max(Number(match[2]) - 1, 0),
  };
}

function hasWordListIgnoreStyle(element: Element) {
  return WORD_LIST_IGNORE_PATTERN.test(element.getAttribute('style') ?? '');
}

function getWordListIgnoreMarkerText(paragraph: HTMLParagraphElement) {
  const ignoreMarker = [...paragraph.querySelectorAll('span')].find((span) =>
    hasWordListIgnoreStyle(span)
  );

  return ignoreMarker?.textContent?.replace(/\u00a0/g, ' ').trim() ?? '';
}

function getWordListClassKind(paragraph: HTMLParagraphElement) {
  if (/\bMsoListBullet\b/i.test(paragraph.className)) return 'ul';
  if (/\bMsoListNumber\b/i.test(paragraph.className)) return 'ol';

  return null;
}

function getListKindFromMarker(markerText: string, isWordListMarker = false) {
  const text = markerText.replace(/\u00a0/g, ' ').trim();

  if (WORD_BULLET_MARKER_PATTERN.test(`${text} `)) return 'ul';
  if (/^\d+[.)]\s*/.test(text)) return 'ol';
  if (/^[a-zA-Z][.)]\s*/.test(text)) return 'ol';

  if (isWordListMarker && /^o\s*/i.test(text)) {
    return 'ul';
  }

  return null;
}

function getLooseListParagraph(
  paragraph: HTMLParagraphElement,
  previousLevel: number | null = null,
  wordListStyles = new Map<string, 'ul' | 'ol'>()
): LooseListParagraph | null {
  const text = paragraph.textContent?.replace(/\u00a0/g, ' ').trim() ?? '';
  const wordListHint = getWordListHint(paragraph);
  const wordListIgnoreMarker = getWordListIgnoreMarkerText(paragraph);

  if (!text) return null;

  if (wordListHint) {
    const kind =
      getListKindFromMarker(wordListIgnoreMarker, true) ??
      getWordListClassKind(paragraph) ??
      wordListStyles.get(`${wordListHint.listId}:${wordListHint.level}`) ??
      getListKindFromMarker(text) ??
      'ol';

    return {
      paragraph,
      kind,
      level: wordListHint.level,
    };
  }

  if (WORD_BULLET_MARKER_PATTERN.test(text)) {
    return { paragraph, kind: 'ul', level: 0 };
  }

  if (/^\d+[.)]\s+/.test(text)) {
    return { paragraph, kind: 'ol', level: 0 };
  }

  const letterMatch = text.match(/^([a-zA-Z])[.)]\s+/);

  if (letterMatch) {
    const marker = letterMatch[1]?.toLowerCase() ?? '';
    const isRomanMarker = /^[ivxlcdm]+$/.test(marker);
    const level = isRomanMarker && previousLevel === 1 ? 2 : 1;

    return { paragraph, kind: 'ol', level };
  }

  return null;
}

function removeWordListIgnoreMarkers(paragraph: HTMLParagraphElement) {
  paragraph.querySelectorAll('span').forEach((span) => {
    if (hasWordListIgnoreStyle(span)) {
      span.remove();
    }
  });
}

function removeLeadingWhitespace(paragraph: HTMLParagraphElement) {
  const walker = paragraph.ownerDocument.createTreeWalker(
    paragraph,
    NodeFilter.SHOW_TEXT
  );
  let node = walker.nextNode();

  while (node) {
    if (node instanceof Text) {
      const value = node.textContent ?? '';
      const strippedValue = value.replace(/^(\s|\u00a0)+/, '');

      node.textContent = strippedValue;

      if (strippedValue) break;
    }

    node = walker.nextNode();
  }
}

function stripLooseListMarker(paragraph: HTMLParagraphElement) {
  removeWordListIgnoreMarkers(paragraph);
  removeLeadingWhitespace(paragraph);

  const walker = paragraph.ownerDocument.createTreeWalker(
    paragraph,
    NodeFilter.SHOW_TEXT
  );
  const textNodes: Text[] = [];
  let combinedText = '';

  let node = walker.nextNode();

  while (node) {
    if (node instanceof Text) {
      textNodes.push(node);
      combinedText += node.textContent ?? '';
    }

    node = walker.nextNode();
  }

  const markerMatch = combinedText.match(
    /^(\s|\u00a0)*([•·●○◦▪▫\uf0b7\uf0a7\uf0d8]|\d+[.)]|[a-zA-Z][.)])(\s|\u00a0)+/
  );

  if (!markerMatch?.[0]) {
    return paragraph.innerHTML;
  }

  let remainingCharactersToRemove = markerMatch[0].length;

  for (const textNode of textNodes) {
    const value = textNode.textContent ?? '';

    if (remainingCharactersToRemove >= value.length) {
      remainingCharactersToRemove -= value.length;
      textNode.textContent = '';

      continue;
    }

    textNode.textContent = value.slice(remainingCharactersToRemove);

    break;
  }

  return paragraph.innerHTML;
}

function createListItem(document: Document, contentHtml: string) {
  const item = document.createElement('li');
  const paragraph = document.createElement('p');

  paragraph.innerHTML = contentHtml;
  item.append(paragraph);

  return item;
}

function getOrCreateNestedList(
  document: Document,
  item: HTMLLIElement,
  kind: 'ul' | 'ol'
) {
  const existingList = [...item.children].find((child) =>
    kind === 'ul'
      ? child instanceof HTMLUListElement
      : child instanceof HTMLOListElement
  );

  if (
    existingList instanceof HTMLUListElement ||
    existingList instanceof HTMLOListElement
  ) {
    return existingList;
  }

  const list = document.createElement(kind);

  item.append(list);

  return list;
}

function appendLooseListItem(
  rootList: HTMLUListElement | HTMLOListElement,
  item: LooseListParagraph,
  contentHtml: string,
  stack: HTMLLIElement[]
) {
  const document = rootList.ownerDocument;
  let level = item.level;

  if (level > 0 && !stack[level - 1]) {
    level = 0;
  }

  const listItem = createListItem(document, contentHtml);

  if (level === 0) {
    rootList.append(listItem);
  } else {
    const parentItem = stack[level - 1];

    if (!parentItem) {
      rootList.append(listItem);
      stack[0] = listItem;
      stack.length = 1;

      return;
    }

    const nestedList = getOrCreateNestedList(document, parentItem, item.kind);

    nestedList.append(listItem);
  }

  stack[level] = listItem;
  stack.length = level + 1;
}

function getLastListItem(list: Element) {
  const lastElement = list.lastElementChild;

  return lastElement instanceof HTMLLIElement ? lastElement : null;
}

function hasLooseListSignal(paragraph: HTMLParagraphElement) {
  const text = paragraph.textContent?.replace(/\u00a0/g, ' ').trim() ?? '';

  return (
    Boolean(getWordListHint(paragraph)) ||
    /\bMsoList/i.test(paragraph.className) ||
    WORD_BULLET_MARKER_PATTERN.test(text) ||
    /^\d+[.)]\s+/.test(text) ||
    /^[a-zA-Z][.)]\s+/.test(text)
  );
}

function getLooseListContainers(document: Document) {
  const containers = new Set<Element>([document.body]);

  document.querySelectorAll('p').forEach((paragraph) => {
    if (!(paragraph instanceof HTMLParagraphElement)) return;
    if (!hasLooseListSignal(paragraph)) return;

    const parent = paragraph.parentElement;

    if (!parent || parent instanceof HTMLLIElement) return;

    containers.add(parent);
  });

  return [...containers];
}

function convertLooseListParagraphsInContainer(
  container: Element,
  wordListStyles: Map<string, 'ul' | 'ol'>
) {
  const children = [...container.children];
  let activeRootList: HTMLUListElement | HTMLOListElement | null = null;
  let activeStack: HTMLLIElement[] = [];
  let previousLooseLevel: number | null = null;

  children.forEach((child) => {
    if (
      child instanceof HTMLUListElement ||
      child instanceof HTMLOListElement
    ) {
      if (
        activeRootList &&
        activeRootList.tagName === child.tagName &&
        activeRootList.nextElementSibling === child
      ) {
        [...child.children].forEach((item) => activeRootList?.append(item));
        child.remove();
      } else {
        activeRootList = child;
      }

      const lastItem = getLastListItem(activeRootList);

      activeStack = lastItem ? [lastItem] : [];
      previousLooseLevel = 0;

      return;
    }

    if (!(child instanceof HTMLParagraphElement)) {
      activeRootList = null;
      activeStack = [];
      previousLooseLevel = null;

      return;
    }

    const looseListParagraph = getLooseListParagraph(
      child,
      previousLooseLevel,
      wordListStyles
    );

    if (!looseListParagraph) {
      activeRootList = null;
      activeStack = [];
      previousLooseLevel = null;

      return;
    }

    if (
      activeRootList &&
      looseListParagraph.level === 0 &&
      activeRootList.tagName.toLowerCase() !== looseListParagraph.kind
    ) {
      activeRootList = null;
      activeStack = [];
    }

    if (!activeRootList) {
      activeRootList = document.createElement(looseListParagraph.kind);
      activeStack = [];
      child.before(activeRootList);
    }

    appendLooseListItem(
      activeRootList,
      looseListParagraph,
      stripLooseListMarker(child),
      activeStack
    );
    previousLooseLevel = looseListParagraph.level;
    child.remove();
  });
}

function convertLooseListParagraphs(document: Document) {
  const wordListStyles = getWordListStyleMap(document);

  getLooseListContainers(document).forEach((container) => {
    convertLooseListParagraphsInContainer(container, wordListStyles);
  });
}

export function normalizePastedHtml(value: string) {
  if (!value.trim()) return value;

  const parser = new DOMParser();
  const document = parser.parseFromString(value, 'text/html');
  const shouldNormalizeWordHtml = isWordLikeHtml(value);
  const shouldDemoteDocumentHeadings = isForeignDocumentHtml(value);

  removeUnsafeLinkHrefsFromDocument(document);
  removeUnsupportedPasteEmbeds(document);
  normalizePastedHeadings(document, shouldDemoteDocumentHeadings);

  if (shouldNormalizeWordHtml) {
    convertWordHorizontalRules(document);
    convertWordQuotes(document);
    convertReferencedWordBookmarks(document);
    removeEmptyWordPasteParagraphs(document);
    convertLooseListParagraphs(document);
  }

  return document.body.innerHTML;
}
